import { ChangeEvent, FormEvent, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { acceptTermsOfUse } from './api/TermsOfUseApi';
import ModalContext from '../../contexts/ModalContext';
import useRouting from '../../hooks/useRouting';
import useThrowAsyncError from '../../hooks/useThrowAsyncError';
import useLogout from '../../hooks/useLogout';
import { Dialog, ModalDialog } from '@pelckmans/business-components/components/dialog';
import { Label } from '@pelckmans/business-components/components/label';
import { Checkbox } from '@pelckmans/business-components/components/checkbox';
import { Button } from '@pelckmans/business-components/components/button';
import { RadioButton } from '@pelckmans/business-components/components/radio-button';

enum Age {
  YOUNGER = 'younger',
  OLDER = 'older',
}

type FormState = {
  age?: string;
  isTermsAndConditionsAccepted?: boolean;
  isPrivacyPolicyAccepted?: boolean;
};

type TermsOfUseModalProps = {
  id: string;
  description: string;
};

const TermsOfUseModal = ({ id, description }: TermsOfUseModalProps) => {
  const { REACT_APP_P_STUDIO_GROUP } = process.env;
  const { t } = useTranslation();
  const { r } = useRouting();
  const handleError = useThrowAsyncError();

  const logOut = useLogout();
  const { closeModal } = useContext(ModalContext);

  const [formState, setFormState] = useState<FormState>({});

  const isVOStudioGroup = REACT_APP_P_STUDIO_GROUP === 'VO';

  const handleFormAge = (event: ChangeEvent<HTMLInputElement>) => {
    setFormState(prevState => ({ ...prevState, age: event.target.value }));
  };

  const toggleTermsAndConditionsAcceptance = () => {
    setFormState(prevState => ({
      ...prevState,
      isTermsAndConditionsAccepted: !prevState.isTermsAndConditionsAccepted,
    }));
  };

  const togglePrivacyPolicyAcceptance = () => {
    setFormState(prevState => ({ ...prevState, isPrivacyPolicyAccepted: !prevState.isPrivacyPolicyAccepted }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    await acceptTermsOfUse(REACT_APP_P_STUDIO_GROUP).catch(handleError);

    closeModal(id);
  };

  const { age, isTermsAndConditionsAccepted, isPrivacyPolicyAccepted } = formState;

  const submitDisabled = (!isVOStudioGroup && !age) || !isTermsAndConditionsAccepted || !isPrivacyPolicyAccepted;

  const formId = 'terms-of-use';

  return (
    <ModalDialog
      id={id}
      closeTitle={t('SERVICES.TERMS_OF_USE_DIALOG.ACTIONS.LOG_OFF')}
      onClose={() => closeModal(id)}
      showCloseButton={false}
    >
      <Dialog.Header title={t('SERVICES.TERMS_OF_USE_DIALOG.TITLE')} />
      <Dialog.Body>
        <p>{description}</p>
        <form noValidate id={formId} onSubmit={handleSubmit} name={formId}>
          {!isVOStudioGroup && (
            <div className="form-group">
              <p>{t('SERVICES.TERMS_OF_USE_DIALOG.AGE.LABEL')}</p>
              <Label htmlFor="age-older">
                <RadioButton
                  id="age-older"
                  name="age"
                  value={Age.OLDER}
                  isChecked={formState.age === Age.OLDER}
                  onChange={handleFormAge}
                />
                <span>{t('SERVICES.TERMS_OF_USE_DIALOG.AGE.OLDER')}</span>
              </Label>
              <br />
              <Label htmlFor="age-younger">
                <RadioButton
                  id="age-younger"
                  name="age"
                  value={Age.YOUNGER}
                  isChecked={formState.age === Age.YOUNGER}
                  onChange={handleFormAge}
                />
                <span>{t('SERVICES.TERMS_OF_USE_DIALOG.AGE.YOUNGER')}</span>
              </Label>
            </div>
          )}
          <div className="form-group">
            <p>{t('SERVICES.TERMS_OF_USE_DIALOG.ACCEPT.LABEL')}</p>
            <Label htmlFor="terms-and-conditions">
              <Checkbox
                id="terms-and-conditions"
                name="terms-and-conditions"
                value="terms-and-conditions"
                status={isTermsAndConditionsAccepted ? 'checked' : 'unchecked'}
                onChange={toggleTermsAndConditionsAcceptance}
              />
              <span>
                {t('SERVICES.TERMS_OF_USE_DIALOG.ACCEPT.PREFIX')}
                &nbsp;
                <a href={r('terms-and-conditions')}>{t('SERVICES.TERMS_OF_USE_DIALOG.ACCEPT.TERMS_AND_CONDITIONS')}</a>
              </span>
            </Label>
            <br />
            <Label htmlFor="privacy-policy">
              <Checkbox
                id="privacy-policy"
                name="privacy-policy"
                value="privacy-policy"
                status={isPrivacyPolicyAccepted ? 'checked' : 'unchecked'}
                onChange={togglePrivacyPolicyAcceptance}
              />
              <span>
                {t('SERVICES.TERMS_OF_USE_DIALOG.ACCEPT.PREFIX')}
                &nbsp;
                <a href={r('privacy-policy')}>{t('SERVICES.TERMS_OF_USE_DIALOG.ACCEPT.PRIVACY_POLICY')}</a>
              </span>
            </Label>
          </div>
        </form>
      </Dialog.Body>
      <Dialog.Footer>
        <Button variant="secondary" onClick={logOut}>
          {t('SERVICES.TERMS_OF_USE_DIALOG.ACTIONS.LOG_OFF')}
        </Button>
        <Button type="submit" disabled={submitDisabled} form={formId} variant="primary">
          {t('SERVICES.TERMS_OF_USE_DIALOG.ACTIONS.ACCEPT')}
        </Button>
      </Dialog.Footer>
    </ModalDialog>
  );
};

export default TermsOfUseModal;
